'use client';

import React, { useState } from 'react';
import { 
  FileText, 
  Download, 
  Copy, 
  Mail, 
  Link2, 
  FileType,
  Loader2,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useExport } from '@/hooks/useExport';
import { useI18n } from '@/lib/i18n';
import { motion } from 'framer-motion';
import { useUsage } from '@/contexts/usage-context';
import { toast } from 'sonner';

interface ExportEpisodeDialogProps {
  content: any;
  cleanReadData?: any;
  isOpen: boolean;
  onClose: () => void;
}

interface ExportOptionProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  onAction: () => Promise<void> | void;
  isLoading?: boolean;
  variant?: 'default' | 'primary';
  locked?: boolean;
}

const ExportOption: React.FC<ExportOptionProps> = ({
  icon: Icon,
  title,
  description,
  onAction,
  isLoading = false,
  variant = 'default',
  locked = false,
}) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="w-full"
    >
      <Button
        variant="outline"
        onClick={async () => {
          if (isLoading) return;
          await onAction();
        }}
        disabled={isLoading}
        className={`
          w-full h-auto p-4 justify-start text-left overflow-hidden
          ${variant === 'primary' 
            ? 'border-primary/40 bg-primary/5 hover:bg-primary/10' 
            : 'hover:bg-muted/50'
          }
          ${locked ? 'border-dashed opacity-90' : ''}
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <div className="flex items-start space-x-3 w-full min-w-0">
          <div className={`
            w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
            ${variant === 'primary' 
              ? 'bg-primary/15 text-primary' 
              : 'bg-muted text-muted-foreground'
            }
          `}>
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Icon className="w-5 h-5" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="font-medium text-foreground mb-1 truncate">
              {title}
            </div>
            <div className="text-sm text-muted-foreground line-clamp-2 break-words">
              {description}
            </div>
          </div>
          {locked && (
            <Lock className="w-4 h-4 flex-shrink-0 text-primary" />
          )}
        </div>
      </Button>
    </motion.div>
  );
};

interface QuickActionProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onAction: () => Promise<void> | void;
  isLoading?: boolean;
  locked?: boolean;
}

const QuickAction: React.FC<QuickActionProps> = ({
  icon: Icon,
  label,
  onAction,
  isLoading = false,
  locked = false,
}) => {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={async () => {
        if (isLoading) return;
        await onAction();
      }}
      disabled={isLoading}
      className="w-full h-12 flex-col gap-1 px-2"
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
      ) : (
        <Icon className={`w-4 h-4 flex-shrink-0 ${locked ? 'text-primary' : ''}`} />
      )}
      <span className="text-xs truncate w-full text-center">{label}</span>
    </Button>
  );
};

export const ExportEpisodeDialog: React.FC<ExportEpisodeDialogProps> = ({
  content,
  cleanReadData,
  isOpen,
  onClose,
}) => {
  const {
    exportToFormat,
    copyAsText,
    shareUrl,
    exportToEmail,
    isExporting,
    currentFormat,
  } = useExport();

  const [quickActionLoading, setQuickActionLoading] = useState<string | null>(null);
  const { usageInfo } = useUsage();
  const { t } = useI18n();

  const isPremium = usageInfo?.isPremium === true || usageInfo?.planType === 'premium';
  const isBasicLite = usageInfo?.isBasicPlanLite === true || usageInfo?.planType === 'basic_lite';
  const isFreePlanUser = !isPremium && !isBasicLite;

  const episodeTitle = content?.content?.title || content?.title || 'Episode';
  const lockedDescription = t(
    'episode.exportDialog.lockedDescription',
    'Export is available on premium plans. Upgrade to download, copy, or share full episode content.'
  );
  const dialogTitle = t('episode.exportDialog.title', 'Export Episode');
  const dialogDescription = t(
    'episode.exportDialog.description',
    'Export "{title}" in your preferred format'
  ).replace('{title}', episodeTitle);
  const quickActionsTitle = t('episode.exportDialog.quickActionsTitle', 'Quick Actions');
  const downloadTitle = t('episode.exportDialog.downloadTitle', 'Download Files');
  const quickActionLabels = {
    copyMarkdown: t('episode.exportDialog.quickActions.copyMarkdown', 'Copy Markdown'),
    copyText: t('episode.exportDialog.quickActions.copyText', 'Copy Text'),
    copyUrl: t('episode.exportDialog.quickActions.copyUrl', 'Copy URL'),
    email: t('episode.exportDialog.quickActions.email', 'Email'),
  };
  const exportOptionText = {
    markdown: {
      title: t('episode.exportDialog.options.markdown.title', 'Markdown File'),
      description: t(
        'episode.exportDialog.options.markdown.description',
        'Download as formatted Markdown (.md) — Perfect for documentation and note-taking'
      ),
    },
    text: {
      title: t('episode.exportDialog.options.text.title', 'Plain Text File'),
      description: t(
        'episode.exportDialog.options.text.description',
        'Download as simple text (.txt) — Universal compatibility'
      ),
    },
    json: {
      title: t('episode.exportDialog.options.json.title', 'JSON Data'),
      description: t(
        'episode.exportDialog.options.json.description',
        'Download structured data (.json) — For developers and automation'
      ),
    },
  };
  const episodeInfoText = {
    episodeLabel: t('episode.exportDialog.episodeInfo.episodeLabel', 'Episode'),
    authorLabel: t('episode.exportDialog.episodeInfo.authorLabel', 'Author'),
    unknownAuthor: t('episode.exportDialog.episodeInfo.unknownAuthor', 'Unknown'),
    includesPrefix: t('episode.exportDialog.episodeInfo.includesPrefix', 'Includes'),
    includesSummary: t('episode.exportDialog.episodeInfo.includesSummary', 'Summary'),
    includesChapters: t('episode.exportDialog.episodeInfo.includesChapters', 'Chapters'),
    includesKeyPoints: t('episode.exportDialog.episodeInfo.includesKeyPoints', 'Key Points'),
    includesTranscript: t('episode.exportDialog.episodeInfo.includesTranscript', 'Full Transcript'),
  };
  const includesParts = [
    episodeInfoText.includesSummary,
    episodeInfoText.includesChapters,
  ];
  if (cleanReadData?.breakdown) {
    includesParts.push(episodeInfoText.includesKeyPoints);
  }
  if (content?.content?.transcript) {
    includesParts.push(episodeInfoText.includesTranscript);
  }
  const includesText = `${episodeInfoText.includesPrefix}: ${includesParts.join(', ')}`;

  const notifyLockedAccess = () => {
    toast.info(lockedDescription);
  };

  const runIfAllowed = async (fn: () => Promise<any> | any) => {
    if (isFreePlanUser) {
      notifyLockedAccess();
      return;
    }
    return await fn();
  };

  const handleQuickAction = async (action: () => Promise<boolean>, actionId: string) => {
    if (isFreePlanUser) {
      notifyLockedAccess();
      return;
    }
    setQuickActionLoading(actionId);
    try {
      await action();
    } finally {
      setQuickActionLoading(null);
    }
  };

  const exportOptions = [
    {
      id: 'markdown',
      icon: FileText,
      title: exportOptionText.markdown.title,
      description: exportOptionText.markdown.description,
      onAction: () => runIfAllowed(() => exportToFormat('markdown', content, cleanReadData)),
      variant: 'primary' as const,
      locked: isFreePlanUser
    },
    {
      id: 'text',
      icon: FileType,
      title: exportOptionText.text.title,
      description: exportOptionText.text.description,
      onAction: () => runIfAllowed(() => exportToFormat('text', content, cleanReadData)),
      locked: isFreePlanUser
    },
    {
      id: 'json',
      icon: Download,
      title: exportOptionText.json.title,
      description: exportOptionText.json.description,
      onAction: () => runIfAllowed(() => exportToFormat('json', content, cleanReadData)),
      locked: isFreePlanUser
    }
  ];

  const quickActions = [
    {
      id: 'copy-markdown',
      icon: Copy,
      label: quickActionLabels.copyMarkdown,
      action: () => copyAsText(content, cleanReadData, 'markdown'),
      locked: isFreePlanUser
    },
    {
      id: 'copy-text',
      icon: FileText,
      label: quickActionLabels.copyText,
      action: () => copyAsText(content, cleanReadData, 'text'),
      locked: isFreePlanUser
    },
    {
      id: 'share-url',
      icon: Link2,
      label: quickActionLabels.copyUrl,
      action: shareUrl,
      locked: isFreePlanUser
    },
    {
      id: 'email',
      icon: Mail,
      label: quickActionLabels.email,
      action: async () => {
        exportToEmail(content, cleanReadData);
        return true;
      },
      locked: isFreePlanUser
    }
  ];

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden rounded-[28px] sm:rounded-[35px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-3 text-xl sm:text-2xl">
            <Download className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">{dialogTitle}</span>
          </DialogTitle>
          <DialogDescription className="pr-3 text-xs sm:text-sm">
            {/* <span className="line-clamp-2">
              {dialogDescription}
            </span> */}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6 overflow-y-auto max-h-[60vh] pr-1 sm:pr-2">
          {/* Quick Actions */}
          <div>
            <h4 className="text-xs sm:text-sm font-medium text-foreground mb-2 sm:mb-3">{quickActionsTitle}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2">
              {quickActions.map((action) => (
                <QuickAction
                  key={action.id}
                  icon={action.icon}
                  label={action.label}
                  onAction={() => handleQuickAction(action.action, action.id)}
                  isLoading={quickActionLoading === action.id}
                  locked={action.locked}
                />
              ))}
            </div>
          </div>

          {/* Download Options */}
          <div>
            <h4 className="text-xs sm:text-sm font-medium text-foreground mb-2 sm:mb-3">{downloadTitle}</h4>
            <div className="space-y-2.5 sm:space-y-3">
              {exportOptions.map((option) => (
                <ExportOption
                  key={option.id}
                  icon={option.icon}
                  title={option.title}
                  description={option.description}
                  onAction={option.onAction}
                  isLoading={isExporting && currentFormat === option.id}
                  variant={option.variant}
                  locked={option.locked}
                />
              ))}
            </div>
          </div>

          {/* Episode Info */}
          <div className="pt-3 sm:pt-4 border-t border-border/50">
            <div className="text-[11px] sm:text-xs text-muted-foreground space-y-1">
              <div className="truncate">{episodeInfoText.episodeLabel}: {episodeTitle}</div>
              <div className="truncate">
                {episodeInfoText.authorLabel}: {content?.content?.author || content?.author || episodeInfoText.unknownAuthor}
              </div>
              {/* <div className="break-words">{includesText}</div> */}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
